import _, { get } from "lodash";
import React, { useMemo, useEffect, useState, useRef } from "react";
import CurrencyFormat from "react-currency-format";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useClient } from "../../../hooks/use-client";
import { SafeMoment } from "../../../components/SafeMoment";
import PrintCommunication from "../components/print/PrintCommunication";
import { ChargesTable } from "../components/ChargesTable";
import { Header, Segment, Image, Placeholder, Divider, Button } from "semantic-ui-react";
import { getConversation } from "../../../queries/comments";
import { useReactToPrint } from "react-to-print";
import networkSelectors from "../../../selectors/network";
import usePermitContacts from "../hooks/usePermitContacts";
import { FormProvider, useForm } from "react-hook-form";
import PermitsForm from "../components/Form";
import { useEmployees } from "../../../queries/employees";
import PrintWorkflows from "../components/print/PrintWorkflows";
import PrintTransactions from "../components/print/PrintTransactions";
import "./PermitPrinting.scss";
import usePermitType from "../hooks/permits/usePermitType";
import usePermit from "../hooks/permits/usePermit";
import { usePermitAttachments } from "../hooks/usePermitAttachments";

export const PermitPrinting = () => {
  const { site, id, printType } = useParams<{ site: string; id: string; printType: string }>();
  const networkHeaders = useSelector(networkSelectors.defaultHeaders);
  const permitQuery = usePermit({ permitId: id });
  const permitsTypeId = useMemo(() => _.get(permitQuery.data, "data.attributes.permits-type-id"), [permitQuery.data]);
  const permitsTypeQuery = usePermitType({ permitTypeId: permitsTypeId });
  const { contacts } = usePermitContacts({ permitId: id });
  const activeFees = useMemo(
    () => _.get(permitQuery.data, "data.attributes[total-active-fee-in-cents]"),
    [permitQuery.data],
  );
  const paid = useMemo(() => _.get(permitQuery.data, "data.attributes[total-paid-in-cents]"), [permitQuery.data]);
  const transactions = useMemo(() => _.get(permitQuery.data, "data.attributes.transactions"), [permitQuery.data]);
  const permitsTypeName = useMemo(() => _.get(permitsTypeQuery.data, "data.attributes.name"), [permitsTypeQuery.data]);
  const workflowSteps = useMemo(() => _.get(permitQuery.data, "data.attributes.steps]"), [permitQuery.data]);
  const employees = useEmployees();
  const balance = useMemo(() => _.get(permitQuery.data, "data.attributes[balance-due]"), [permitQuery.data]);
  const feeList = useMemo(() => _.get(permitQuery.data, "data.attributes.fee-details"), [permitQuery.data]);
  const issuedDate = useMemo(() => _.get(permitQuery.data, "data.attributes.issued-at"), [permitQuery.data]);
  const expiredDate = useMemo(() => _.get(permitQuery.data, "data.attributes.expired-at"), [permitQuery.data]);
  const startDate = useMemo(
    () => _.get(permitQuery.data, "data.attributes.started-at", new Date()),
    [permitQuery.data],
  );
  const submittedDate = useMemo(() => _.get(permitQuery.data, "data.attributes.created-at"), [permitQuery.data]);
  const applicationNumber = useMemo(
    () => _.get(permitQuery.data, "data.attributes.confirmation-number"),
    [permitQuery.data],
  )
    ?.match(/.{3}/g)
    ?.join("-");
  const formalNumber = useMemo(() => _.get(permitQuery.data, "data.attributes.reservation-number"), [permitQuery.data]);
  const dynamicNumber = formalNumber || applicationNumber;
  const identifier = useMemo(() => _.get(permitQuery.data, "data.attributes.primary-identifier"), [permitQuery.data]);
  const client = useClient(site);
  const cityName = useMemo(() => _.get(client.query, "data.payload.data.attributes.name"), [client.query.data]);
  const citySeal = useMemo(() => _.get(client.query, "data.payload.data.attributes.seal"), [client.query.data]);
  const today = new Date().toLocaleDateString("en-US");
  const conversationQuery = useQuery(
    ["conversation", id.toString()],
    async () => await getConversation(networkHeaders)(id, "Permit"),
  );
  const comments = get<{ attributes: Comment }[]>(conversationQuery.data, "data.attributes.comments.data", []).map(
    (commentData) => commentData.attributes,
  );
  const applicant = contacts?.find((contact: any) => contact?.isApplicant);
  const applicantName = `${applicant?.firstName || ""} ${applicant?.lastName || ""}`;
  const permitsEmployees = (employees.data || []).filter((employee: any) => !!employee.attributes["permits-role"]);
  const permitForm = useForm({
    shouldUnregister: false,
  });

  const { data: permitAttachmentData, isLoading: attachmentsLoading } = usePermitAttachments({ permitId: id });
  const internalAttachments = useMemo(() => {
    return (
      permitAttachmentData?.data?.map((attachment) => ({
        id: attachment.id,
        name: attachment.attributes["file-name"],
        signedUrl: attachment.attributes["signed-url"],
      })) ?? []
    );
  }, [permitAttachmentData]);
  const formatDate = (date: string) => {
    if (date) {
      return <SafeMoment format='MMM D, YYYY'>{date}</SafeMoment>;
    } else {
      return "--";
    }
  };

  const isLoading =
    permitQuery.isLoading || permitsTypeQuery.isLoading || conversationQuery.isLoading || attachmentsLoading;

  // Defining a state, which is set in PermitsFormSection component, to get the form field values so that attachment fields can be displayed in the appendix
  const [formFields, setFormFields] = useState([]);
  const attachmentFields = formFields.filter(
    (field: any) => Array.isArray(field?.value) && field.value.length > 0 && field.value[0].signed_url,
  );

  const printOptions = [
    {
      label: "Applicant",
      content: applicantName,
    },
    {
      label: "Application #",
      content: applicationNumber,
    },
    {
      label: "Submitted",
      content: formatDate(submittedDate),
    },
    {
      label: "Issued",
      content: formatDate(issuedDate),
    },
    {
      label: "Starts",
      content: formatDate(startDate),
    },
    {
      label: "Expires",
      content: formatDate(expiredDate),
    },
    {
      label: "Identifier",
      content: identifier || "--",
    },
  ];

  // Custom print function using react-to-print
  const componentRef = useRef(null);
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  // The readyToPrint state is set in the PermitForm, so that it only prints once all form fields have rendered
  const [readyToPrint, setReadyToPrint] = useState(false);

  // Auto open the print dialog after the page renders
  useEffect(() => {
    if (!readyToPrint || isLoading) return;
    handlePrint();
  }, [readyToPrint, attachmentFields]);

  // Overriding the command / control + p shortcut to use the custom handlePrint function
  window.addEventListener("keydown", function (event) {
    if (event.key === "p" && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      handlePrint();
    }
  });

  return (
    <>
      <Button className='print-statement-button' primary icon='print' content='Print' onClick={() => handlePrint()} />
      <div className='print-wrapper' ref={componentRef}>
        <Segment className='top-segment' vertical>
          <Header as='h3'>
            <Image src={citySeal} size='big' />
            <Header.Content className='wrap-text' style={{ maxWidth: "40vw", wordWrap: "break-word" }}>
              {cityName}
            </Header.Content>
          </Header>
          <div>
            <Header
              as='h3'
              content={printType == "printout" ? "PRINTOUT" : "STATEMENT"}
              subheader={`Printed: ${today}`}
              style={{ textAlign: "right" }}
            />
          </div>
        </Segment>
        {isLoading ? (
          <>
            <Placeholder fluid>
              <Placeholder.Line />
              <Placeholder.Line />
            </Placeholder>
            <Placeholder fluid>
              <Placeholder.Line />
              <Placeholder.Line />
              <Placeholder.Image />
            </Placeholder>
            <Placeholder fluid>
              <Placeholder.Image />
            </Placeholder>
          </>
        ) : (
          <>
            <Segment vertical className='middle-segment'>
              <div>
                <Header as='h4' className='unBold' content={permitsTypeName} />
                <Header content={dynamicNumber} />
              </div>
              <div style={{ textAlign: "right" }}>
                <Header as='h4' className='unBold' content={"Balance"} />
                <CurrencyFormat
                  style={{ fontSize: 18, fontWeight: "bold" }}
                  value={balance / 100}
                  fixedDecimalScale={true}
                  decimalScale={2}
                  displayType={"text"}
                  thousandSeparator={true}
                  prefix={"$"}
                />
              </div>
            </Segment>
            <Segment vertical className='summary-content'>
              {printOptions.map((option: any) => {
                return (
                  <div key={option.label}>
                    <label> {option.label} </label>
                    <p> {option.content} </p>
                  </div>
                );
              })}
            </Segment>
            {printType == "printout" && (
              <FormProvider {...permitForm}>
                <PermitsForm
                  setPrintableFormFields={setFormFields}
                  isPrintView={true}
                  setPrint={() => setReadyToPrint(true)}
                  permitId={id}
                  permitsTypeId={permitsTypeId}
                  isPublicView={true}
                  readonly
                />
              </FormProvider>
            )}
            <Segment vertical className='printable-tables'>
              <Header content='Fees' />
              <ChargesTable
                subtotal={permitQuery.data?.data.attributes.subtotal}
                print={true}
                feeList={feeList}
                activeFees={activeFees}
                balance={balance}
                paid={paid}
                pending={0}
              />
            </Segment>
            {transactions.length > 0 && <PrintTransactions transactions={transactions} />}
            {printType == "printout" && (
              <>
                <Divider />
                {comments && <PrintCommunication comments={comments} />}
                <Divider />
                <PrintWorkflows workflowSteps={workflowSteps} permitsEmployees={permitsEmployees} />
                {(internalAttachments.length > 0 || attachmentFields.length > 0) && (
                  <>
                    <Header content='Appendix' className='image-header' />
                    <div className='image-container'>
                      {internalAttachments.map((attachment, index: number) => {
                        return (
                          <div key={index}>
                            <Image bordered fluid src={attachment.signedUrl} />
                            {`Internal Attachment - ${attachment.name}`}
                          </div>
                        );
                      })}
                      {attachmentFields.map((attachment: any) => {
                        return attachment.value.map((attachment: any, index: number) => {
                          return (
                            <div key={index}>
                              <Image key={index} bordered fluid src={attachment.url} />
                              {attachment.name}
                            </div>
                          );
                        });
                      })}
                    </div>
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default PermitPrinting;
